<template>
    <div>
        <ConfirmDialog
            :is-open="confirmRemoveProductDialog"
            :action-loading="actionLoading.removeProduct"
            @on-confirm="removeProduct"
            @on-abort="closeConfirmRemoveProductDialog"
        >
            <template #title>
                Usuwanie produktu
            </template>
            <template #content>
                Czy na pewno chcesz usunąć produkt? Tej operacji nie można cofnąć.
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-container fluid class="pa-0">
            <v-row>
                <v-col cols="12" md="4" lg="3">
                    <v-text-field
                        v-model="name"
                        label="Nazwa kalkulacji"
                        outlined
                        dense
                        :error-messages="calculationNameErrors"
                        required
                        :readonly="!isCalculationEditable"
                        @change="temporarySaveCalculationData"
                    />
                </v-col>
                <v-col cols="12" md="6" lg="4">
                    <v-autocomplete
                        v-model="customer"
                        label="Klient"
                        :items="customersFound"
                        :filter="customFilter"
                        :loading="actionLoading.customerSearchLoading"
                        :search-input.sync="customerSearchQuery"
                        class="v-text-field--placeholder-dark"
                        item-text="name"
                        return-object
                        outlined
                        dense
                        :readonly="!isCalculationEditable"
                        :error-messages="customerIdErrors"
                        required
                        no-data-text="Nie znaleziono"
                        @change="changeCustomer"
                    >
                        <template slot="no-data">
                            <v-list-item>
                                <v-list-item-title>
                                    <template v-if="!customerSearchQuery">
                                        Wpisz treść aby odnaleźć <strong>klienta</strong>.
                                    </template>
                                    <template v-else-if="customerSearchQuery.length < customerSearchQueryMinLength">
                                        Wpisz przynajmniej <strong>{{ customerSearchQueryMinLength }}</strong> znaki.
                                    </template>
                                    <template v-else-if="actionLoading.customerSearchLoading">
                                        Trwa wyszukiwanie klienta...
                                    </template>
                                    <template v-else>
                                        Nie odnaleziono klienta na zapytanie: "<strong> {{ customerSearchQuery }}".</strong>
                                    </template>
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>

            <v-row align="center">
                <v-col cols="auto">
                    <h2 class="text-h6">
                        Produkty
                    </h2>
                </v-col>
                <v-col cols="auto">
                    <v-btn
                        v-if="isCalculationEditable"
                        color="secondary"
                        fab
                        small
                        class="elevation-1"
                        data-sentry-label="Dodaj produkt"
                        @click="addProduct"
                    >
                        <v-icon>
                            mdi-plus
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-data-table
                        v-if="hasProducts"
                        :headers="headers"
                        :items="calculationProducts"
                        disable-pagination
                        hide-default-footer
                        :mobile-breakpoint="0"
                        item-key="index"
                    >
                        <template #item.productType="{item}">
                            {{ item.productType.name }}
                        </template>
                        <template #item.productName="{item}">
                            {{ item.name }}
                        </template>
                        <template #item.formatA="{item}">
                            {{ parseEmptyValue(item.formatA) }}
                        </template>
                        <template #item.formatB="{item}">
                            {{ parseEmptyValue(item.formatB) }}
                        </template>
                        <template #item.amount="{item}">
                            {{ item.amount }}
                        </template>
                        <template #item.isInImposition="{item}">
                            <v-icon
                                v-if="isProductInImposition(item)"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon
                                v-else
                                small
                                color="error"
                            >
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip v-if="!isCalculationEditable" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz produkt"
                                            v-on="on"
                                            @click="openProduct(item.id)"
                                        >
                                            <v-icon color="primary">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip v-else :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Edytuj produkt"
                                            v-on="on"
                                            @click="openProduct(item.id)"
                                        >
                                            <v-icon color="teal">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                                <v-tooltip v-if="isCalculationEditable" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Kopiuj produkt"
                                            v-on="on"
                                            @click="copyProduct(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-content-copy
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Kopiuj</span>
                                </v-tooltip>
                                <v-tooltip v-if="isCalculationEditable" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showConfirmRemoveProductDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>

                    <v-alert
                        v-else
                        type="info"
                        text
                        border="left"
                    >
                        Nie wybrano produktów do kalkulacji.
                    </v-alert>
                </v-col>
            </v-row>

            <v-row no-gutters justify="end">
                <v-col cols="auto">
                    <v-btn
                        color="primary"
                        depressed
                        class="mt-3 mb-2"
                        :disabled="!hasProducts"
                        data-sentry-label="Następny krok"
                        @click="nextStep"
                    >
                        Następny krok
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import router from '@/router'

import ConfirmDialog from '@/components/common/ConfirmDialog'

import customFilters from '@/mixins/customFilters'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import { required } from 'vuelidate/lib/validators'

let customerSearchTimer

export default {
    name: 'CalculationParameters',
    components: {
        ConfirmDialog
    },
    mixins: [
        customFilters,
        tooltipDelay,
        parse,
    ],
    props: {
        isVisible: {
            type: Boolean
        },
        isCalculationEditable: {
            type: Boolean
        },
        papers: {
            type: Array,
            default: null
        },
    },
    data() {
        return {
            name: null,
            customerSearchQuery: null,
            customerSearchQueryMinLength: 3,
            customersFound: [],
            customer: null,
            customerId: null,
            calculationProducts: [],
            currentProductId: null,
            confirmRemoveProductDialog: false,
            actionLoading: {
                customerSearchLoading: false,
                removeProduct: false
            },
            headers: [
                {
                    text: 'Rodzaj produktu',
                    value: 'productType',
                    sortable: false,
                },
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    sortable: false,
                },
                {
                    text: 'Szerokość',
                    value: 'formatA',
                    align: 'center',
                    sortable: false,
                    width: 140
                },
                {
                    text: 'Wysokość',
                    value: 'formatB',
                    align: 'center',
                    sortable: false,
                    width: 140
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 140
                },
                {
                    text: 'Czy przydzielony?',
                    value: 'isInImposition',
                    align: 'center',
                    sortable: false,
                    width: 140
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ]
        }
    },
    validations() {
        return {
            name: { required },
            customerId: { required }
        }
    },
    computed: {
        calculationId() {
            return this.$store.state.calculations.calculations.calculation.id
        },
        calculationImpositions() {
            return this.$store.state.calculations.calculations.calculation.calculationImpositions
        },
        hasProducts() {
            return this.calculationProducts && this.calculationProducts.length > 0
        },
        // Walidacje
        calculationNameErrors() {
            const errors = []

            if (!this.$v.name.$dirty) return errors
            !this.$v.name.required && errors.push('Proszę podać nazwę.')

            return errors
        },
        customerIdErrors() {
            const errors = []

            if (!this.$v.customerId.$dirty) return errors
            !this.$v.customerId.required && errors.push('Proszę wybrać klienta.')

            return errors
        },
    },
    watch: {
        isVisible(boolean) {
            if (boolean) {
                this.calculationProducts = JSON.parse(JSON.stringify(this.$store.state.calculations.calculations.calculation.calculationProducts))
            }
        },
        customerSearchQuery(value = null) {
            if (value === null || value.length < this.customerSearchQueryMinLength || value === this.customer?.name) return

            this.actionLoading.customerSearchLoading = true

            clearTimeout(customerSearchTimer)

            customerSearchTimer = setTimeout(() => {
                if (value) {
                    this.searchCustomers(value)
                } else {
                    this.actionLoading.customerSearchLoading = false
                    this.customersFound = []
                }
            }, 500)
        }
    },
    created() {
        this.name = this.$store.state.calculations.calculations.calculation.name
        this.customer = this.$store.state.calculations.calculations.calculation.customer

        if (this.customer) {
            this.customerId = this.customer.id
            this.customersFound.push(this.customer)
        }

        this.calculationProducts = JSON.parse(JSON.stringify(this.$store.state.calculations.calculations.calculation.calculationProducts))
    },
    methods: {
        searchCustomers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie klienta')

            this.$store.dispatch('customers/getCustomers', {
                paginate: '?limit=20',
                query: `&isHidden=false&name=${query}`
            })
                .then(response => {
                    this.customersFound = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.customerSearchLoading = false
                })
        },
        changeCustomer(customer) {
            if (customer) {
                this.customerId = customer.id
                this.temporarySaveCalculationData()
            } else {
                this.customerId = null
            }
        },
        addProduct() {
            router.push({
                name: 'calculationsCalculationAddProduct'
            })
                .catch(error => errorHandler(error))
        },
        openProduct(productId) {
            router.push({
                name: 'calculationsCalculationProduct',
                params: {
                    productId: productId
                }
            })
                .catch(error => errorHandler(error))
        },
        copyProduct(product) {
            this.$store.dispatch('calculations/copyCalculationProduct', product)

            this.$emit('calculation-loading', true)

            this.$store.dispatch('calculations/updateCalculation', this.$store.state.calculations.calculations.calculation)
                .then(response => {
                    this.calculationProducts = JSON.parse(JSON.stringify(response.data.calculationProducts))
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        removeProduct() {
            this.actionLoading.removeProduct = true

            this.$store.dispatch('calculations/removeCalculationProduct', this.currentProductId)

            this.$store.dispatch('calculations/updateCalculation', this.$store.state.calculations.calculations.calculation)
                .then(response => {
                    this.calculationProducts = JSON.parse(JSON.stringify(response.data.calculationProducts))
                    this.confirmRemoveProductDialog = false
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.removeProduct = false
                })
        },
        nextStep() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.$emit('next-step')
        },
        showConfirmRemoveProductDialog(product) {
            if (this.isProductInImposition(product)) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można usunąć produktu, który jest przydzielony do montażu.'
                })
            } else if (this.isProductInPacking(product)) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie można usunąć produktu, który jest przydzielony do pakowania.'
                })
            } else {
                this.currentProductId = product.id
                this.confirmRemoveProductDialog = true
            }
        },
        closeConfirmRemoveProductDialog() {
            this.currentProductId = null
            this.confirmRemoveProductDialog = false
        },
        temporarySaveCalculationData() {
            this.$store.dispatch('calculations/temporarySaveCalculationData', {
                name: this.name,
                customer: this.customer,
                customerId: this.customerId
            })
        },
        isProductInImposition(product) {
            return product.calculationProductComponents.some(item => {
                return item.calculationImpositionId !== null
            })
        },
        isProductInPacking(product) {
            const packageWares = []

            this.calculationImpositions.forEach(imposition => {
                imposition.calculationImpositionTasks.forEach(task => {
                    task.calculationImpositionTaskPackages?.forEach(singlePackage => {
                        singlePackage.calculationImpositionTaskPackageWares?.forEach(packageWare => {
                            packageWares.push(packageWare)
                        })
                    })
                })
            })

            return packageWares.some(packageWare => packageWare.calculationProductId === product.id)
        }
    }
}
</script>